import { BookOpen, ExternalLink } from 'lucide-react'
import physicsModelUrl from '../../docs/physics-model.md?url'
import { HorizontalScrollRegion } from './HorizontalScrollRegion'
import { ModelBadge, Notice, Section } from './ui'

export function ModelTab() {
  return (
    <div className="tab-stack prose-tab">
      <div className="result-header">
        <div>
          <ModelBadge>model boundary</ModelBadge>
          <h1>Model and assumptions</h1>
          <p>Horizontal optical cross-section through straight vertical cylindrical lenticules.</p>
        </div>
        <a className="secondary-button" href={physicsModelUrl} target="_blank" rel="noreferrer"><BookOpen size={15} /> Open physics-model.md</a>
      </div>

      <Notice tone="warning" title="Glass thickness alone cannot determine compatibility">
        Thickness changes propagation, but source pitch, lens pitch, optical power or geometry, refractive indices, phase, apertures, viewing geometry, and tolerances remain independent inputs. Missing quantities are reported rather than replaced with typical values.
      </Notice>

      <Section title="Numerical model labels" description="A result keeps the label of the engine that produced it. Results are not silently blended across rows.">
        <HorizontalScrollRegion className="table-scroll" label="Numerical model definitions">
          <table className="data-table model-table">
            <thead><tr><th>Label in this application</th><th>What it calculates</th><th>Key limits</th></tr></thead>
            <tbody>
              <tr><td><ModelBadge>analytical first-order estimate</ModelBadge></td><td>Required finite-conjugate focal length, pitch, LPI, zone spacing, image distance, drift, and sensitivities.</td><td>Paraxial, ideal thin lens, viewing-side air, no finite aperture or aberration.</td></tr>
              <tr><td><ModelBadge>paraxial thin-lens simulation</ModelBadge></td><td>Traced source and lens-aperture samples, viewer intensity, pupil transfer matrix, crosstalk, desired transmission.</td><td>Uses u = nθ and ideal thin-lens power. Angles are paraxial.</td></tr>
              <tr><td><ModelBadge>geometric thick-lens ray trace</ModelBadge></td><td>Exact flat-layer travel, circular-surface intersection, vector Snell refraction, TIR, viewer intensity, and focal characterization.</td><td>Two-dimensional meridional geometry; circular plano-convex surface only.</td></tr>
              <tr><td><ModelBadge>Monte Carlo tolerance result</ModelBadge></td><td>Seeded uncertainty trials, conditional criteria success, percentiles, and failure association.</td><td>Conditional on entered distributions, independence assumption, selected model, and visible criteria.</td></tr>
              <tr><td><ModelBadge>measured or calibration-fitted result</ModelBadge></td><td>Imported intensity curves, measured centers/FWHM/crosstalk, and explicit fitted center geometry.</td><td>Fit quality depends on measurement sampling and the stated fit objective.</td></tr>
            </tbody>
          </table>
        </HorizontalScrollRegion>
      </Section>

      <div className="split-sections">
        <Section title="Coordinate and reference conventions">
          <ul className="assumption-list">
            <li>x is positive to the right; propagation is from the LCD toward the viewer.</li>
            <li>Internal distances are millimeters and internal angles are radians.</li>
            <li>Paraxial rays use [x,u], where u = nθ. A flat layer translates x by (t/n)u.</li>
            <li>d = Σtᵢ/nᵢ is reduced axial distance, not optical path length Σnᵢtᵢ.</li>
            <li>Thin-lens z is measured from the thin-lens plane. Thick-lens z is measured from the viewer-facing front vertex.</li>
            <li>The thick-lens optical stack ends at the rear flat surface. Center thickness is traced once inside the lens.</li>
            <li>Positive source offsets form zones on the opposite side of the axis; transfer results report the desired source assignment.</li>
          </ul>
        </Section>
        <Section title="Included geometry">
          <ul className="assumption-list">
            <li>Locally flat LCD and lens sheet.</li>
            <li>Vertical lenticule axes with horizontal refraction.</li>
            <li>Viewing plane perpendicular to the main axis.</li>
            <li>Flat, homogeneous, isotropic intermediate layers.</li>
            <li>Periodic straight lenslets around the display center and across the active-width sample.</li>
            <li>Flat, Lambertian, or Gaussian-HWHM relative source angular weighting.</li>
          </ul>
        </Section>
      </div>

      <Section title="Explicit omissions" description="These omissions apply to every v1 result unless a future model says otherwise.">
        <div className="omission-grid">
          <span>Diffraction</span><span>Polarization-dependent transmission</span><span>Scattering</span><span>Coherent interference</span><span>Panel electronics</span><span>Temporal multiplexing</span><span>Eye tracking</span><span>Fresnel reflection losses</span><span>Absolute radiometry</span>
        </div>
        <p className="result-note">Relative intensity is accepted weighted ray energy divided by launched weighted ray energy. It is not absolute luminance or photometric transmission.</p>
      </Section>

      <Notice tone="danger" title="Slanted lenticules are not modeled in v1">
        Slanted displays require a two-dimensional source lattice, color-subpixel routing, and view assignment model. This application does not offer a slant slider or a cosine correction.
      </Notice>

      <Section title="Reference trail" description="The full equation derivations, convention choices, regression checks, and source list are recorded in the project document.">
        <p className="prose-copy">
          Primary and authoritative references include OpenStax lens and Snell-law treatments, MIT and RIT matrix-optics notes, SPIE lenticular-display design material, PBRT’s vector refraction and ray-intersection derivations, Edmund Optics focal-length conventions, and SCHOTT dispersion documentation.
        </p>
        <a className="text-link" href={physicsModelUrl} target="_blank" rel="noreferrer">Read the full physics model and citations <ExternalLink size={14} /></a>
      </Section>
    </div>
  )
}
